import { ScrollState, useScrollRig } from "@14islands/r3f-scroll-rig";
import { Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useThree } from "@react-three/fiber";
import React, {
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Material } from "three";
//@ts-ignore
import { clamp } from "three/src/math/MathUtils";

/**
 * Returns a WebGL Troika text mesh styled as the source DOM element
 */

interface WebGLTextProps {
  el: MutableRefObject<HTMLElement>;
  children?: ReactNode;
  material?: Material;
  scale?: any;
  font?: string;
  fontOffsetY?: number;
  fontOffsetX?: number;
  overrideEmissive?: boolean;
  color?: string;
  scrollState: ScrollState;
}

export const WebGLText = ({
  el,
  children,
  material,
  scale,
  font,
  fontOffsetY = 0,
  fontOffsetX = 0,
  overrideEmissive = false,
  color,
  ...props
}: WebGLTextProps) => {
  const ref = useRef() as MutableRefObject<any>;
  const { size } = useThree();
  const { scaleMultiplier } = useScrollRig();

  const { textColor, fontSize, textAlign, lineHeight, letterSpacing } =
    useMemo(() => {
      if (!el.current) return {};
      const cs = window.getComputedStyle(el.current);

      // get color from parent if set to transparent
      let textColor = color || cs.color;
      if (
        !color &&
        cs.color === "rgba(0, 0, 0, 0)" &&
        el.current.parentElement
      ) {
        textColor = window.getComputedStyle(el.current.parentElement).color;
      }

      // font size relative letter spacing
      const letterSpacing =
        (parseFloat(cs.letterSpacing) || 0) / parseFloat(cs.fontSize);
      const lineHeight =
        (parseFloat(cs.lineHeight) || 0) / parseFloat(cs.fontSize);

      return {
        letterSpacing,
        lineHeight,
        textColor,
        fontSize: parseFloat(cs.fontSize) * scaleMultiplier,
        textAlign: cs.textAlign,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el, size, scale, color, scaleMultiplier]); // recalc on resize

  useEffect(() => {
    if (material && overrideEmissive) {
      // @ts-ignore
      material.emissive = color;
    }
  }, [material, color, overrideEmissive]);

  let xOffset = 0;
  if (textAlign === "left" || textAlign === "start") {
    xOffset = scale[0] * -0.5;
  } else if (textAlign === "right" || textAlign === "end") {
    xOffset = scale[0] * 0.5;
  }

  const yOffset = scale ? scale[1] * 0.5 : size.height * 0.5;

  useFrame(({ clock }) => {
    if (!ref.current) return;

    // visibility is 0 when image enters viewport and 1 when fully visible
    ref.current.material.grayscale = clamp(
      1 - props.scrollState.visibility ** 3,
      0,
      1
    );

    // viewport is 0 when image enters and 1 when image reach top of screen
    ref.current.material.opacity = clamp(props.scrollState.viewport * 3, 0, 1);
  });

  return (
    <Text
      ref={ref}
      fontSize={fontSize}
      maxWidth={scale ? scale[0] : size.width}
      lineHeight={lineHeight}
      // @ts-ignore
      textAlign={textAlign}
      letterSpacing={letterSpacing}
      overflowWrap="break-word"
      font={font}
      color={textColor}
      // @ts-ignore
      anchorX={textAlign}
      anchorY="top" // so text moves down if row breaks
      // @ts-ignore
      position={[
        xOffset + fontSize! * fontOffsetX,
        yOffset + fontSize! * fontOffsetY,
        0,
      ]} // font specific
      material={material}
      {...props}
    >
      {children}
    </Text>
  );
};
